/* eslint-disable no-unused-vars */
import { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Autocomplete,
  TextField,
} from '@mui/material';
import { useEffect } from 'react';
import { useRouteLoaderData } from 'react-router-dom';
import proxy from '@/services/ProxyService';
import { toast } from 'react-toastify';
import ToastMessage from '@/components/toasts/ToastMessage';
import useApiMutation from '@/hooks/useApiMutation';
import { DataGridPro } from '@mui/x-data-grid-pro';
import useFormData from '@/hooks/useFormData';

const RETURN_STATUS = ['CANCELLED', 'CLOSED', 'MANUALLY_CLOSED'];

export default function BucketForm({ data: bucket }) {
  const { config, users } = useRouteLoaderData('auth');
  const { mutateAsync: updateBucket } = useApiMutation('bucket', bucket._id);
  const [salesInvoices, setSalesInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const { formData, handleChange, reset } = useFormData(bucket);
  useEffect(() => {
    async function getInvoiceId() {
      const { data: invoiceRes } = await proxy.get(
        `${config.warehouse.weclappInterface}/salesInvoice?customerNumber-eq=${bucket.customerNumber}&salesInvoiceType-eq=STANDARD_INVOICE&status-eq=NEW`,
      );
      setSalesInvoices(invoiceRes?.result || []);
    }

    getInvoiceId();
  }, [bucket]);

  const itemsById = {};
  for (const item of bucket?.items || []) {
    let { id, serialNumber, ...rest } = item;
    if (!id) {
      id = `ITEMWITHNOIT-${Math.random().toString(36).substr(2, 16)}`;
      itemsById[id] = {
        ...rest,
        id,
        articleNumber: '- - -',
        name: item?.description,
        serialNumbers: [],
      };
    }
    if (!itemsById[id]) {
      itemsById[id] = { ...rest, id, serialNumbers: [] };
    }
    if (serialNumber) {
      itemsById[id].serialNumbers.push(serialNumber);
    }
  }
  const rows = Object.values(itemsById).map((item) => {
    if (item.serialNumbers.length === 0) {
      return item;
    }
    return {
      ...item,
      quantity: item.serialNumbers.length,
    };
  });
  const [selectedRowIds, setSelectedRowIds] = useState(
    rows.map((row) => row.id),
  );

  useEffect(() => {
    setSelectedRowIds(rows.map((row) => row.id));
  }, [bucket]);

  const handleBilling = async () => {
    const orderNumber = bucket?.orderNumber || null;
    const selectedRows = rows.filter((r) => selectedRowIds.includes(r.id));
    if (orderNumber) {
      try {
        const { data: salesOrderRes } = await proxy.get(
          `${config.warehouse.weclappInterface}/salesOrder?orderNumber-eq=${orderNumber}`,
        );
        let salesOrder = salesOrderRes?.result[0] || null;
        if (!salesOrder) {
          toast.error(<ToastMessage title={'Auftrag nicht gefunden!'} />);
          return;
        }
        if (RETURN_STATUS.includes(salesOrder.status)) {
          toast.error(
            <ToastMessage title={'Auftrag kann nicht Abgerechnet werden!'} />,
          );
          return;
        }
        if (salesOrder.status === 'ORDER_CONFIRMATION_PRINTED') {
          try {
            const { data: res } = await proxy.put(
              `${config.warehouse.weclappInterface}/salesOrder/id/${salesOrder.id}?ignoreMissingProperties=true`,
              { ...salesOrder, status: 'ORDER_ENTRY_IN_PROGRESS' },
            );
            salesOrder.version = res.version;
            salesOrder.status = 'ORDER_ENTRY_IN_PROGRESS';
          } catch (error) {
            toast.error(
              <ToastMessage
                title={'Verantwortliche Person des Auftrages nicht gefunden!'}
              />,
            );
            return;
          }
        }
        salesOrder.orderItems = salesOrder.orderItems ?? [];

        for (const row of selectedRows) {
          const existingItem = salesOrder.orderItems.find(
            (item) => item.articleId === row.id,
          );

          if (existingItem) {
            existingItem.quantity = parseFloat(existingItem.quantity || 0) + 1;

            if (row.serialNumbers?.length) {
              for (const sn of row.serialNumbers) {
                const { data: serialnumberRes } = await proxy.get(
                  `${config.warehouse.weclappInterface}/serialNumber?serialNumber-eq=${sn}`,
                );
                const serialNumberId = serialnumberRes?.result[0]?.id;
                let storagePlaceId = null;

                if (serialNumberId) {
                  const { data: warehouseStockRes } = await proxy.get(
                    `${config.warehouse.weclappInterface}/warehouseStock?serialNumbers.id-in=[${serialNumberId}]`,
                  );
                  storagePlaceId =
                    warehouseStockRes?.result[0]?.storagePlaceId || null;
                }

                existingItem.picks = existingItem.picks ?? [];

                const existingPick = existingItem.picks.find(
                  (p) => p.storagePlaceId === storagePlaceId,
                );

                if (existingPick) {
                  existingPick.quantity =
                    parseFloat(existingPick.quantity || 0) + 1;
                  existingPick.serialNumbers = existingPick.serialNumbers ?? [];
                  if (!existingPick.serialNumbers.includes(sn)) {
                    existingPick.serialNumbers.push(sn);
                  }
                } else {
                  existingItem.picks.push({
                    quantity: 1,
                    serialNumbers: [sn],
                    storagePlaceId,
                  });
                }
              }
            }
          } else {
            const newOrderItem = {
              articleId: row.id,
              quantity: row.quantity,
              itemType: 'DEFAULT',
            };
            if (row.serialNumbers?.length) {
              newOrderItem.picks = [];
              for (const sn of row.serialNumbers) {
                const { data: serialnumberRes } = await proxy.get(
                  `${config.warehouse.weclappInterface}/serialNumber?serialNumber-eq=${sn}`,
                );
                const serialNumberId = serialnumberRes?.result[0]?.id;
                let storagePlaceId = null;
                if (serialNumberId) {
                  const { data: warehouseStockRes } = await proxy.get(
                    `${config.warehouse.weclappInterface}/warehouseStock?serialNumbers.id-in=[${serialNumberId}]`,
                  );
                  storagePlaceId =
                    warehouseStockRes?.result[0]?.storagePlaceId || null;
                }
                const existingPick = newOrderItem.picks.find(
                  (p) => p.storagePlaceId === storagePlaceId,
                );
                if (existingPick) {
                  existingPick.quantity =
                    parseFloat(existingPick.quantity || 0) + 1;
                  existingPick.serialNumbers = existingPick.serialNumbers ?? [];
                  if (!existingPick.serialNumbers.includes(sn)) {
                    existingPick.serialNumbers.push(sn);
                  }
                } else {
                  newOrderItem.picks.push({
                    quantity: 1,
                    serialNumbers: [sn],
                    storagePlaceId,
                  });
                }
              }
            }
            salesOrder.orderItems.push(newOrderItem);
          }
        }
        try {
          const { data: newSalesOrderRes } = await proxy.put(
            `${config.warehouse.weclappInterface}/salesOrder/id/${salesOrder.id}?ignoreMissingProperties=true`,
            salesOrder,
          );
          salesOrder = newSalesOrderRes;
        } catch (error) {
          toast.error(
            <ToastMessage
              title={'Fehler beim ergänzen der Artikel zum Auftrag!'}
            />,
          );
          return;
        }
        try {
          const { data: successfullSalesOrder } = await proxy.put(
            `${config.warehouse.weclappInterface}/salesOrder/id/${salesOrder.id}?ignoreMissingProperties=true`,
            { ...salesOrder, status: 'ORDER_CONFIRMATION_PRINTED' },
          );
          await updateBucket(
            { ...bucket, status: 'BILLED' },
            'bucket',
            bucket._id,
            false,
          );
          toast.success(
            <ToastMessage title={'Auftragsbestätigung erzeugt!'} />,
          );
        } catch (error) {
          toast.error(
            <ToastMessage
              title={'Fehler beim erzeugen der Auftragsbestätigung!'}
            />,
          );
          return;
        }
        // eslint-disable-next-line no-empty
      } catch (error) {}
    } else {
      // Check if invoice exists
      if (!selectedInvoice) {
        toast.error(<ToastMessage title={'Keine Rechnung ausgewählt!'} />);
        return;
      }

      let customer = null;

      // Check if customer exists
      try {
        const { data: partyRes } = await proxy.get(
          `${config.warehouse.weclappInterface}/party?customerNumber-eq=${bucket.customerNumber}`,
        );
        const party = partyRes?.result || null;
        if (
          !party ||
          party.length !== 1 ||
          !party[0].customerNumber === bucket.customerNumber
        )
          throw new Error('Ungültige Kunden-Nr.!');
        customer = party[0];
      } catch (error) {
        toast.error(<ToastMessage title={'Ungültige Kunden-Nr.!'} />);
        return;
      }

      let currentInvoice = null;
      // Get the newest invoice version
      try {
        const { data: invoiceRes } = await proxy.get(
          `${config.warehouse.weclappInterface}/salesInvoice/id/${selectedInvoice.id}`,
        );
        if (!invoiceRes) throw new Error('Rechnung nicht mehr vorhanden!');
        currentInvoice = invoiceRes;
      } catch (error) {
        toast.error(<ToastMessage title={'Rechnung nicht mehr vorhanden!'} />);
        return;
      }

      // Build items for new shipment
      const shipmentItems = [];
      for (const row of selectedRows) {
        const newOrderItem = {
          articleId: row.id,
          quantity: row.quantity,
          itemType: 'DEFAULT',
          picks: [],
        };
        if (row.serialNumbers?.length) {
          for (const sn of row.serialNumbers) {
            const { data: serialnumberRes } = await proxy.get(
              `${config.warehouse.weclappInterface}/serialNumber?serialNumber-eq=${sn}`,
            );
            const serialNumberId = serialnumberRes?.result[0]?.id;
            let storagePlaceId = null;
            if (serialNumberId) {
              const { data: warehouseStockRes } = await proxy.get(
                `${config.warehouse.weclappInterface}/warehouseStock?serialNumbers.id-in=[${serialNumberId}]`,
              );
              storagePlaceId =
                warehouseStockRes?.result[0]?.storagePlaceId || null;
            }
            const existingPick = newOrderItem.picks.find(
              (p) => p.storagePlaceId === storagePlaceId,
            );
            if (existingPick) {
              existingPick.quantity =
                parseFloat(existingPick.quantity || 0) + 1;
              existingPick.serialNumbers = existingPick.serialNumbers ?? [];
              if (!existingPick.serialNumbers.includes(sn)) {
                existingPick.serialNumbers.push(sn);
              }
            } else {
              newOrderItem.picks.push({
                quantity: 1,
                serialNumbers: [sn],
                storagePlaceId,
              });
            }
          }
        } else if (row.storagePlaceId) {
          newOrderItem.picks.push({
            quantity: row.quantity,
            serialNumbers: [],
            storagePlaceId: row.storagePlaceId,
          });
        }
        shipmentItems.push(newOrderItem);
      }

      // Create new shipment
      try {
        const { data: newShipment } = await proxy.post(
          `${config.warehouse.weclappInterface}/shipment`,
          {
            recipientPartyId: customer.id,
            warehouseId: bucket?.warehouseId,
            shipmentItems,
            shipmentType: 'STANDARD',
            status: 'DELIVERY_NOTE_PRINTED',
          },
        );

        // Update shipment status so that shipment is delivered
        try {
          await proxy.put(
            `${config.warehouse.weclappInterface}/shipment/id/${newShipment.id}`,
            {
              ...newShipment,
              status: 'SHIPPED',
            },
          );
        } catch (error) {
          toast.error(
            <ToastMessage
              title={'Lieferung konnte nicht ausgeliefert werden!'}
            />,
          );
          return;
        }

        // Add items to the selected invoice
        try {
          await proxy.put(
            `${config.warehouse.weclappInterface}/salesInvoice/id/${currentInvoice.id}?ignoreMissingProperties=true`,
            {
              salesInvoiceItems: [
                ...currentInvoice.salesInvoiceItems,
                ...newShipment.shipmentItems.map((item) => ({
                  articleId: item.articleId,
                  quantity: item.quantity,
                  description: `Zu Ticket #${bucket.ticketID}`,
                })),
              ],
            },
          );
          await updateBucket(
            { ...bucket, status: 'BILLED' },
            'bucket',
            bucket._id,
            false,
          );
          toast.success(
            <ToastMessage title={'Lieferung erzeugt und Rechnung ergänzt!'} />,
          );
        } catch (error) {
          toast.error(
            <ToastMessage
              title={'Lieferung konnte der Rechnung nicht hinzugefügt werden!'}
            />,
          );
          return;
        }
      } catch (error) {
        toast.error(
          <ToastMessage title={'Lieferung konnte nicht erzeugt werden!'} />,
        );
        return;
      }
    }
  };

  const columns = [
    { field: 'articleNumber', headerName: 'Art.-Nr.', flex: 1 },
    { field: 'quantity', headerName: 'Menge', flex: 1 },
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'serialNumbers', headerName: 'S/N', flex: 1 },
  ];

  const bucketUser = users.find((u) => u._id === bucket.user);
  return (
    <Box
      sx={{
        width: '100%',
        padding: 2,
        display: 'flex',
        flexDirection: 'column',
        position: 'absolute',
        top: 0,
        gap: 2,
        px: 3,
        left: 0,
        right: 0,
      }}
    >
      <Typography variant="h6" gutterBottom>
        Bucket Übersicht
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            gap: 1,
          }}
        >
          <Typography gutterBottom>
            {`Kd.-Nr.: ${formData.customerNumber}`}
          </Typography>
          <Typography
            gutterBottom
          >{`Kd.-Name: ${formData.contact}`}</Typography>
          {formData.orderNumber && (
            <Typography
              gutterBottom
            >{`Auftrags-Nr.: ${formData.orderNumber || ''}`}</Typography>
          )}
          {formData.ticketID && (
            <Typography
              gutterBottom
            >{`Ticket-ID: ${formData.ticketID || ''}`}</Typography>
          )}
          <Typography
            gutterBottom
          >{`Techniker: ${bucketUser.firstName} ${bucketUser.lastName}`}</Typography>
        </Box>
        <Box>
          <Typography gutterBottom>
            {`Typ: ${formData.type === 'RECEIVED' ? 'Annahme' : 'Mitnahme'}`}
          </Typography>
          <Typography gutterBottom>
            {`Status: ${
              formData.status === 'BILLED'
                ? 'Abgerechnet'
                : formData.status === 'CLOSED'
                  ? 'Geschlossen'
                  : 'Offen'
            }`}
          </Typography>
        </Box>
      </Box>

      <DataGridPro
        rows={rows}
        columns={columns}
        checkboxSelection
        disableRowSelectionOnClick
        pagination
        pageSizeOptions={[5]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 5 },
          },
          rowSelection: {
            rowSelectionModel: rows.map((row) => row.id),
          },
        }}
        rowSelectionModel={{ ids: new Set(selectedRowIds), type: 'include' }}
        onRowSelectionModelChange={(newSelection) => {
          setSelectedRowIds(Array.from(newSelection.ids || []));
        }}
        getRowId={(row) => row.id}
        isRowSelectable={(params) =>
          !params.id.toString().startsWith('ITEMWITHNOIT')
        }
        sx={{
          height: '100%',
        }}
      />
      {selectedRowIds.length > 0 && bucket.status === 'CLOSED' && (
        <>
          {!bucket.orderNumber && (
            <Autocomplete
              options={salesInvoices}
              getOptionLabel={(option) => `${option?.invoiceNumber || ''}`}
              fullWidth
              value={selectedInvoice}
              onChange={(event, newValue) => {
                setSelectedInvoice(newValue);
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField {...params} label="Rechnung" />
              )}
            />
          )}
          <Button
            variant="outlined"
            onClick={handleBilling}
            disabled={!bucket.orderNumber && selectedInvoice == null}
          >
            Abrechnen
          </Button>
        </>
      )}
      <Box sx={{ marginBottom: 2, display: 'flex', flexDirection: 'column' }}>
        <TextField
          label="Kommentar"
          value={formData.comment}
          onChange={handleChange('comment')}
          multiline
          rows={3}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="outlined" onClick={reset}>
          Verwerfen
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          onClick={async () => await updateBucket(formData)}
        >
          Speichern
        </Button>
      </Box>
    </Box>
  );
}
